import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert
} from '@mui/material';
import {
  Check as CheckIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';

import { useTheme } from '../../contexts/ThemeContext';

interface ThemePresetsProps {
  onClose: () => void;
}

const ThemePresets: React.FC<ThemePresetsProps> = ({ onClose }) => {
  const { currentTheme, allThemePresets, customThemePresets, adminSharedThemes, applyPreset, saveCustomPreset, deleteCustomPreset } = useTheme();
  
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleApplyPreset = async (presetId: string) => {
    await applyPreset(presetId);
    // Optionally close the dialog after applying
    // onClose();
  };

  const isCurrentTheme = (presetTheme: any) => {
    return (
      presetTheme.mode === currentTheme.mode &&
      presetTheme.primaryColor === currentTheme.primaryColor &&
      presetTheme.secondaryColor === currentTheme.secondaryColor &&
      presetTheme.backgroundColor === currentTheme.backgroundColor &&
      presetTheme.cardBackground === currentTheme.cardBackground &&
      presetTheme.textColor === currentTheme.textColor
    );
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      setError('Preset name is required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await saveCustomPreset(presetName.trim(), presetDescription.trim());
      setSuccess('Custom preset saved successfully!');
      setSaveDialogOpen(false);
      setPresetName('');
      setPresetDescription('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to save preset');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePreset = async (presetId: string) => {
    try {
      await deleteCustomPreset(presetId);
      setSuccess('Custom preset deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to delete preset');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleOpenSaveDialog = () => {
    setError(null);
    setSuccess(null);
    setSaveDialogOpen(true);
  };

  const handleCloseSaveDialog = () => {
    setSaveDialogOpen(false);
    setPresetName('');
    setPresetDescription('');
    setError(null);
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Theme Presets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose from presets or save your current theme as a custom preset.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenSaveDialog}
          sx={{ ml: 2 }}
        >
          Save Current Theme
        </Button>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {allThemePresets.map((preset: any) => (
          <Grid item xs={12} sm={6} md={4} key={preset.id}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: isCurrentTheme(preset.theme) ? 2 : 1,
                borderColor: isCurrentTheme(preset.theme) ? 'primary.main' : 'divider',
                position: 'relative',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: (theme) => theme.shadows[4],
                },
              }}
            >
              {/* Current Theme Indicator */}
              {isCurrentTheme(preset.theme) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    backgroundColor: 'primary.main',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
              )}

              <CardContent sx={{ flexGrow: 1 }}>
                {/* Theme Preview */}
                <Box
                  sx={{
                    height: 100,
                    borderRadius: 1,
                    mb: 2,
                    background: `linear-gradient(135deg, ${preset.theme.backgroundColor} 0%, ${preset.theme.cardBackground} 100%)`,
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Color Swatches */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                      display: 'flex',
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: preset.theme.primaryColor,
                        border: '1px solid white',
                      }}
                    />
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: preset.theme.secondaryColor,
                        border: '1px solid white',
                      }}
                    />
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: preset.theme.customColors.accent1,
                        border: '1px solid white',
                      }}
                    />
                  </Box>

                  {/* Mode Icon */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: preset.theme.textColor,
                    }}
                  >
                    {preset.theme.mode === 'dark' ? <DarkModeIcon /> : <LightModeIcon />}
                  </Box>
                </Box>

                {/* Theme Info */}
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6">
                      {preset.name}
                    </Typography>
                    <Chip
                      label={preset.theme.mode}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                    {(preset as any).isCustom && (
                      <Chip 
                        label="Custom" 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                      />
                    )}
                    {(preset as any).isShared && (
                      <Chip 
                        label={`By ${(preset as any).sharedByRole}`}
                        size="small" 
                        color="primary" 
                        variant="filled"
                        sx={{ 
                          fontSize: '0.65rem',
                          height: 20,
                          backgroundColor: 'primary.main',
                          color: 'white'
                        }}
                      />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    {preset.description}
                  </Typography>

                  {/* Color Details */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    <Chip
                      label={preset.theme.primaryColor.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: preset.theme.primaryColor + '20',
                        color: preset.theme.primaryColor,
                        fontSize: '0.7rem',
                        fontFamily: 'monospace',
                      }}
                    />
                    <Chip
                      label={preset.theme.secondaryColor.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: preset.theme.secondaryColor + '20',
                        color: preset.theme.secondaryColor,
                        fontSize: '0.7rem',
                        fontFamily: 'monospace',
                      }}
                    />
                  </Box>
                </Stack>
              </CardContent>

              <CardActions sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={isCurrentTheme(preset.theme) ? 'contained' : 'outlined'}
                  fullWidth
                  onClick={() => handleApplyPreset(preset.id)}
                  disabled={isCurrentTheme(preset.theme)}
                >
                  {isCurrentTheme(preset.theme) ? 'Current Theme' : 'Apply Theme'}
                </Button>
                {(preset as any).isCustom && !(preset as any).isShared && (
                  <IconButton
                    onClick={() => handleDeletePreset(preset.id)}
                    color="error"
                    title="Delete custom preset"
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Custom Theme Info */}
      <Box sx={{ mt: 4, p: 2, backgroundColor: 'action.hover', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          💡 <strong>Tip:</strong> After applying a preset, you can further customize it using the Colors and Layout tabs. 
          Your customizations will be automatically saved. Click "Save Current Theme" to save your customizations as a new preset.
        </Typography>
      </Box>

      {/* Save Custom Preset Dialog */}
      <Dialog open={saveDialogOpen} onClose={handleCloseSaveDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Save Current Theme as Preset</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save your current theme configuration as a custom preset that you can apply later.
          </Typography>
          
          <TextField
            fullWidth
            label="Preset Name"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            placeholder="My Awesome Theme"
            margin="dense"
            required
            error={error?.includes('name')}
            helperText={error?.includes('name') ? error : 'Enter a unique name for your preset'}
          />
          
          <TextField
            fullWidth
            label="Description (Optional)"
            value={presetDescription}
            onChange={(e) => setPresetDescription(e.target.value)}
            placeholder="A brief description of your theme..."
            margin="dense"
            multiline
            rows={2}
          />

          {/* Current Theme Preview */}
          <Box sx={{ mt: 2, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>Preview of Current Theme:</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: currentTheme.primaryColor,
                  border: '1px solid white',
                }}
              />
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: currentTheme.secondaryColor,
                  border: '1px solid white',
                }}
              />
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: currentTheme.customColors.accent1,
                  border: '1px solid white',
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {currentTheme.mode === 'dark' ? 'Dark' : 'Light'} mode • Border radius: {currentTheme.borderRadius}px
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSaveDialog} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSavePreset} 
            variant="contained" 
            disabled={isLoading || !presetName.trim()}
            startIcon={isLoading ? undefined : <SaveIcon />}
          >
            {isLoading ? 'Saving...' : 'Save Preset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThemePresets;